import { SearchFilterField } from '../models/search-filter-spec.model';

/** What a recognized leading field label hints the REST of the query should be matched
    against. `licenseNo` has no vocabulary (see `SearchVocabularyStore`'s class doc — there is
    no bulk license-number list), so its remainder is taken as a raw, case-preserved value
    rather than looked up. `vocabulary` scopes matching to exactly one field (and role, for
    performer/reviewer), instead of the generic multi-field token matching every other query
    goes through. */
export type FieldLabelTarget =
  | { kind: 'licenseNo' }
  | { kind: 'vocabulary'; field: SearchFilterField; role?: 'P' | 'R' };

export interface FieldLabelMatch {
  target: FieldLabelTarget;
  /** Original-case, trimmed remainder after the label and its separator. Empty when the user
      typed the label with nothing after it (e.g. "license no"). Never further tokenized —
      the caller decides how to search it (raw value for licenseNo, whole-phrase match for a
      vocabulary field). */
  rawValue: string;
}

/**
 * Leading field-label patterns a user types before the value they actually want to search
 * ("License no - MH-12/GC/0000/0000", "department: HR", "performer Jane Doe"). Anchored to the
 * START of the (trimmed) query only — mid-query labels are out of scope. Order does not matter:
 * every pattern below is mutually exclusive by construction (no two can match the same prefix).
 */
const FIELD_LABEL_PATTERNS: Array<{ pattern: RegExp; target: FieldLabelTarget }> = [
  { pattern: /^licen[cs]e\s*no\.?(?=[\s:=-]|$)/i, target: { kind: 'licenseNo' } },
  { pattern: /^licen[cs]e\s*number(?=[\s:=-]|$)/i, target: { kind: 'licenseNo' } },
  { pattern: /^lic\s*no\.?(?=[\s:=-]|$)/i, target: { kind: 'licenseNo' } },
  { pattern: /^licen[cs]e\s*type(?=[\s:=-]|$)/i, target: { kind: 'vocabulary', field: 'licenseType' } },
  { pattern: /^status(?=[\s:=-]|$)/i, target: { kind: 'vocabulary', field: 'status' } },
  { pattern: /^dep(?:artmen)?t(?=[\s:=-]|$)/i, target: { kind: 'vocabulary', field: 'department' } },
  { pattern: /^performer(?=[\s:=-]|$)/i, target: { kind: 'vocabulary', field: 'user', role: 'P' } },
  { pattern: /^reviewer(?=[\s:=-]|$)/i, target: { kind: 'vocabulary', field: 'user', role: 'R' } },
  { pattern: /^location(?=[\s:=-]|$)/i, target: { kind: 'vocabulary', field: 'entities' } },
  { pattern: /^state(?=[\s:=-]|$)/i, target: { kind: 'vocabulary', field: 'entities' } },
];

/** Optional single separator (colon, dash or equals, with surrounding whitespace) between the
    label and its value — "license no - X", "license no: X", "license no= X", "license no X"
    are all accepted. */
const SEPARATOR = /^\s*[-:=]?\s*/;

/**
 * Recognizes a leading field-label phrase and splits it from its value. Returns `null` when the
 * query does not start with any known label — the caller falls back to generic multi-field
 * token matching for the whole query (this is how a BARE license number like
 * "MH-12/GC/0000/0000", with no "license no" prefix, is still handled — by the generic path,
 * not by this function).
 */
export function extractFieldLabel(query: string): FieldLabelMatch | null {
  const trimmed = query.trim();
  for (const { pattern, target } of FIELD_LABEL_PATTERNS) {
    const match = pattern.exec(trimmed);
    if (!match) continue;
    const rest = trimmed.slice(match[0].length);
    const rawValue = rest.replace(SEPARATOR, '').trim();
    return { target, rawValue };
  }
  return null;
}
